/**
 * Dashboard Service — fetches bot stats JSON via the authenticated Lambda proxy.
 *
 * All data is pushed by dashboard_s3_sync.py on the bot EC2 every 5 minutes.
 * The Lambda reads from S3 on the server side, enforcing Cognito auth.
 * Types below match the ACTUAL JSON structures in S3.
 */

import { getAuthHeaders } from './authUtils';
import { API_URL } from '../config';

async function fetchDashboard<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/dashboard${path}`, { headers });
  if (!res.ok) throw new Error(`Dashboard API error: ${res.status}`);
  return res.json();
}

/* ── Types matching actual S3 JSON ────────────────────── */

export interface BotPeriodStats {
  total_trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  total_pnl: number;
  avg_win: number;
  avg_loss: number;
  profit_factor: number;
  best_trade: number;
  worst_trade: number;
}

export interface BotEntry {
  today: BotPeriodStats;
  all_time: BotPeriodStats;
  last_trade: string | null;
  has_data: boolean;
}

export interface OverviewPayload {
  date: string;
  today_stats: BotPeriodStats;
  bots: Record<string, BotEntry>;
  prediction: any;
  updated_at: string;
}

/* ── Trades ───────────────────────────────────────────── */

export interface Trade {
  bot: string;
  symbol: string;
  side: string;
  entry_time: string;
  exit_time: string;
  entry_price: number;
  exit_price: number;
  qty: number;
  pnl: number;
  exit_reason: string;
}

export interface TradesPayload {
  bots: Record<string, { trades: Trade[]; stats: BotPeriodStats }>;
  grand_total: BotPeriodStats;
  today: BotPeriodStats;
  updated_at: string;
}

/* ── Pullback backtest ────────────────────────────────── */

export interface BacktestPeriodData {
  metrics: Record<string, any>;
  equity_curve: { date: string; equity: number }[];
  trades: {
    entry_date: string;
    exit_date: string;
    entry_price: number;
    exit_price: number;
    pnl: number;
    pnl_pct: number;
    score: number;
    hold_days: number;
  }[];
}

export interface PullbackBacktestPayload {
  backtest: {
    full: BacktestPeriodData;
    in_sample: BacktestPeriodData;
    out_of_sample: BacktestPeriodData;
  };
  params: Record<string, any>;
  updated_at: string;
}

/* ── Outcomes ─────────────────────────────────────────── */

export interface DistributionBucket {
  lo: number;
  hi: number;
  count: number;
}

export interface StrategyHistoricalStats {
  total_trades: number;
  win_rate: number;
  avg_win?: number;
  avg_loss?: number;
  avg_win_pct?: number;
  avg_loss_pct?: number;
  profit_factor: number;
  sharpe?: number;
  total_pnl?: number;
  max_drawdown_pct?: number;
  best_trade?: number;
  worst_trade?: number;
  best_trade_pct?: number;
  worst_trade_pct?: number;
  trade_pnl_distribution: {
    buckets: DistributionBucket[];
    count: number;
    mean?: number;
    median?: number;
  };
}

export interface StrategyOutcome {
  name: string;
  hold_period: string;
  strategy_type: string;
  historical_stats: StrategyHistoricalStats;
}

export interface OutcomesPayload {
  strategies: StrategyOutcome[];
  prediction: {
    direction: string;
    confidence: number;
    raw_score: number;
    risk_flags: string[];
  };
  significance: Record<string, any>;
  updated_at: string;
}

/* ── Predictions ──────────────────────────────────────── */

export interface PredictionTodayPayload {
  date: string;
  generated_at: string;
  prediction: {
    direction: string;
    confidence: number;
    raw_score: number;
    max_score: number;
    reasoning: string[];
    risk_flags: string[];
  };
  market_data: {
    vix_level: number;
    spy_gap: { gap_pct: number; prev_close: number; current: number };
    spy_trend: { trend: string };
    news: { headline_count: number; bullish_count: number; bearish_count: number; score: number };
    yesterday: { total_pnl: number; total_trades: number; win_rate: number; worst_bot: string; best_bot: string };
  };
  updated_at: string;
}

/* ── Prediction History types ─────────────────────────── */

export interface PredictionAccuracyData {
  total: number;
  correct: number;
  partial: number;
  wrong: number;
  accuracy_pct: number;
  accuracy_7d: number;
  accuracy_30d: number;
  bull_total: number;
  bull_correct: number;
  bull_accuracy_pct: number;
  bear_total: number;
  bear_correct: number;
  bear_accuracy_pct: number;
  current_streak: number;
  streak_type: string;
  calibration: Record<string, { total: number; correct: number; accuracy_pct: number }>;
}

export interface PredictionHistoryRow {
  date: string;
  direction: string;
  confidence: string;
  raw_score: string;
  risk_flags: string;
  spy_prev_close: string;
  spy_open: string;
  spy_close: string;
  actual_direction: string;
  correct: string;
}

export interface PredictionHistoryPayload {
  predictions: PredictionHistoryRow[];
  accuracy: PredictionAccuracyData;
  updated_at: string;
}

/* ── Fetch functions ──────────────────────────────────── */

export const dashboardService = {
  getOverview: () => fetchDashboard<OverviewPayload>('/overview'),
  getTrades: () => fetchDashboard<TradesPayload>('/trades'),
  getEquityCurve: () =>
    fetchDashboard<{ curve: { date: string; equity: number; daily_pnl: number }[] }>('/equity-curve'),
  getPullbackBacktest: () => fetchDashboard<PullbackBacktestPayload>('/pullback-backtest'),
  getPredictionToday: () => fetchDashboard<PredictionTodayPayload>('/prediction-today'),
  getPredictionHistory: () => fetchDashboard<PredictionHistoryPayload>('/prediction-history'),
  getOutcomes: () => fetchDashboard<OutcomesPayload>('/outcomes'),
};
